import { cva } from 'class-variance-authority'
import type { ComponentProps } from 'react'

import Cell from '@/components/atoms/Cell'
import MinusIcon from '@/components/atoms/icons/MinusIcon'
import PlusIcon from '@/components/atoms/icons/PlusIcon'
import VerticalDivider from '@/components/atoms/VerticalDivider'
import useExpandedPortsDialog from '@/hooks/useExpandedPortsDialog'
import useStepperControls from '@/hooks/useStepperControls'
import useWarningDialog from '@/hooks/useWarningDialog'
import type { Item } from '@/stores/CartStore'
import useDialogsStore from '@/stores/DialogsStore'
import { useMobileLayoutStore } from '@/stores/MobileLayoutStore'
import { cn } from '@/util/cn'
import { PORT_EXPANDER_RNUMBER } from '@/util/Hardcoded'

type StepperProps = { item: Item } & ComponentProps<'div'>

export const cellVariants = cva(
  'justify-center border-2 p-2 md:p-[18px]',
  {
    variants: {
      variant: {
        purple: 'border-rsf-purple-light bg-rsf-white text-rsf-accent',
        available: 'border-rsf-green-badge bg-rsf-white text-rsf-green',
        preselect: 'border-rsf-green-badge bg-rsf-white text-rsf-green',
        disabled: 'border-rsf-purple-light bg-rsf-white text-rsf-grey',
        na: 'border-rsf-grey-light bg-rsf-white-background text-rsf-grey-dividers',
      },
    },
  },
)

const buttonVariants = cva(
  '*:size-4 disabled:cursor-not-allowed *:md:size-6',
  {
    variants: {
      variant: {
        purple: 'text-rsf-accent',
        disabled: 'text-rsf-purple-light',
        available: 'text-rsf-green-badge',
      },
    },
  },
)

function Stepper({ item }: StepperProps) {
  const {
    amount,
    decrease,
    decreaseDisabled,
    hideButtons,
    increase,
    increaseDisabled,
    isDisabled,
    isNA,
    preselect,
  } = useStepperControls(item)
  const { isMobile, activeMobileTab } = useMobileLayoutStore()
  const { checkWarningDialog } = useWarningDialog(item)
  const toggleDialog = useDialogsStore(store => store.toggle)
  const expandedPorts = useExpandedPortsDialog(item)
  const dividerClassName = preselect ? 'text-rsf-grey-light' : 'text-rsf-purple-light'

  const handleIncrease = () => {
    if (increaseDisabled) return
    if (checkWarningDialog()) return
    increase()
    expandedPorts.check()
  }

  const handleDecrease = () => {
    if (decreaseDisabled && amount !== 0 && item.rNumber === PORT_EXPANDER_RNUMBER) {
      toggleDialog('portExpanderDisabled')
    }
    if (decreaseDisabled) return
    decrease()
  }

  return (
    <Cell
      className={cellVariants({
        variant: isNA ? 'na' : preselect && amount > 0 ? 'preselect' : isDisabled ? 'disabled' : amount > 0 ? 'available' : 'purple',
        className: cn(
          isNA || hideButtons ? 'justify-center' : 'justify-between',
          isMobile && activeMobileTab === 'deluxe' && 'col-span-2',
        ),
      })}
    >
      {!hideButtons && (
        <div className={cn('flex flex-row items-center lg:gap-1', decreaseDisabled && 'cursor-not-allowed *:cursor-not-allowed')}>
          <button
            type="button"
            onClick={handleDecrease}
            className={buttonVariants({
              variant: decreaseDisabled ? 'disabled' : 'available',
            })}
          >
            <div className="grid size-3 place-content-center">
              <MinusIcon />
            </div>

          </button>
          <VerticalDivider className={dividerClassName} />
        </div>
      )}

      <span className="text-center text-sm font-semibold tabular-nums leading-tight md:text-base">
        {isNA ? 'n/a' : amount}
      </span>

      {!hideButtons && (
        <div className="flex flex-row items-center gap-1">
          <VerticalDivider className={dividerClassName} />
          <button
            type="button"
            onClick={handleIncrease}
            disabled={increaseDisabled}
            className={buttonVariants({
              variant: increaseDisabled ? 'disabled' : amount === 0 ? 'purple' : 'available',
            })}
          >
            <div className="grid size-3 place-content-center">
              <PlusIcon />
            </div>
          </button>
        </div>
      )}

    </Cell>
  )
}

export default Stepper
